import { z } from "zod";
import { Created, Id, Ok, OptionalText } from "./common";
import { defineRoutes } from "./define";
import { PROBLEMS } from "./problem";

/** Mirrors core/modules/episodes/domain/clinical-status.ts. */
export const CLINICAL_STATUSES = [
  "acute",
  "irritable",
  "controlled",
  "return_to_load",
  "maintenance",
] as const;
export const ClinicalStatus = z.enum(CLINICAL_STATUSES);
export type ClinicalStatus = z.infer<typeof ClinicalStatus>;

const Pain = z.number().int().min(0).max(10);

export const EpisodeListItem = z.object({
  id: Id,
  title: z.string(),
  clinicalStatus: ClinicalStatus,
  stage: z.number().int(),
  painNow: Pain.nullable(),
  closedAt: z.string().nullable(),
});
export type EpisodeListItem = z.infer<typeof EpisodeListItem>;

export const CreateEpisodeBody = z.object({
  title: z.string().trim().min(1, "Назва епізоду обовʼязкова").max(160),
  clinicalStatus: ClinicalStatus.optional(),
  stage: z.number().int().min(1).max(6).optional(),
  hypothesis: OptionalText(2000),
  goal: OptionalText(2000),
  painStart: Pain.nullable().optional(),
  constraints: OptionalText(2000),
});
export type CreateEpisodeBody = z.infer<typeof CreateEpisodeBody>;

/** Mirrors core's UpdateEpisodeSchema (the clinical refinement form). */
export const UpdateEpisodeBody = z.object({
  title: z.string().trim().min(1, "Назва епізоду обовʼязкова").max(160),
  clinicalStatus: ClinicalStatus.optional(),
  hypothesis: OptionalText(2000),
  goal: OptionalText(2000),
  painNow: Pain.nullable().optional(),
  functionNow: OptionalText(2000),
  redFlags: OptionalText(2000),
  constraints: OptionalText(2000),
});
export type UpdateEpisodeBody = z.infer<typeof UpdateEpisodeBody>;

export const StageChange = z.object({ from: z.number().int(), to: z.number().int() });

export const episodes = defineRoutes({
  list: {
    method: "GET",
    path: "/patients/:patientId/episodes",
    summary: "A patient's episodes, open and closed",
    params: z.object({ patientId: Id }),
    responses: { 200: z.array(EpisodeListItem), 401: PROBLEMS[401], 403: PROBLEMS[403] },
  },
  create: {
    method: "POST",
    path: "/patients/:patientId/episodes",
    summary: "Open a rehab episode for a patient",
    params: z.object({ patientId: Id }),
    body: CreateEpisodeBody,
    responses: {
      201: Created,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      422: PROBLEMS[422],
    },
  },
  update: {
    method: "PATCH",
    path: "/episodes/:episodeId",
    summary: "Refine the clinical picture (hypothesis, goal, pain now, flags…)",
    params: z.object({ episodeId: Id }),
    body: UpdateEpisodeBody,
    responses: {
      200: Ok,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      404: PROBLEMS[404],
      422: PROBLEMS[422],
    },
  },
  changeStage: {
    method: "POST",
    path: "/episodes/:episodeId/stage",
    summary: "Move the rehab stage by exactly one step",
    params: z.object({ episodeId: Id }),
    body: z.object({ direction: z.enum(["advance", "regress"]) }),
    responses: {
      200: StageChange,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      404: PROBLEMS[404],
      422: PROBLEMS[422],
    },
  },
  changeLifecycle: {
    method: "POST",
    path: "/episodes/:episodeId/lifecycle",
    summary: "Close a finished episode or reopen it",
    params: z.object({ episodeId: Id }),
    body: z.object({ lifecycle: z.enum(["close", "reopen"]) }),
    responses: {
      200: Ok,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      404: PROBLEMS[404],
      422: PROBLEMS[422],
    },
  },
});
